import { useState } from 'react'
import { CombinedError, useMutation, useQuery } from 'urql'
import { copyWhenReady } from 'app/hooks/useCopyToClipboard'
import { parsePacketForms } from 'app/lib/forms/builder'
import type { PatientChoice } from 'app/lib/forms/paperworkRecord'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import { CREATE_PAPERWORK_MUTATION, PAPERWORK_TEMPLATES_QUERY } from 'app/components/paperwork/graphql'

const ALL_PACKETS = { page: 0, limit: 500 }

type UseSendPaperworkReturn = {
  packets: { id: string; name: string }[]
  loadingPackets: boolean
  patients: PatientChoice[]
  packetId: string | undefined
  patientId: string | undefined
  setPacketId: (id: string) => void
  setPatientId: (id: string) => void
  sending: boolean
  send: () => Promise<void>
}

/** A link is minted per patient, so with several patient records the choice is never guessed. */
export function useSendPaperwork(patients: PatientChoice[], onSent: () => void): UseSendPaperworkReturn {
  const [{ data, fetching }] = useQuery({
    query: PAPERWORK_TEMPLATES_QUERY,
    variables: { archived: false, pagination: ALL_PACKETS },
    requestPolicy: `cache-and-network`,
  })
  const [{ fetching: sending }, executeCreate] = useMutation(CREATE_PAPERWORK_MUTATION)
  const packets = (data?.paperworkTemplates.hits ?? [])
    .filter((packet) => parsePacketForms(packet.formsJson).length > 0)
    .map((packet) => ({ id: packet.id, name: packet.name }))
    .sort((a, b) => a.name.localeCompare(b.name))

  const [chosenPacketId, setChosenPacketId] = useState<string>()
  const [chosenPatientId, setChosenPatientId] = useState<string>()
  const packetId = chosenPacketId ?? (packets.length === 1 ? packets[0].id : undefined)
  const patientId = chosenPatientId ?? (patients.length === 1 ? patients[0].id : undefined)

  const send = async () => {
    if (!packetId || !patientId) return
    const link = executeCreate({ input: { paperworkTemplateId: packetId, patientId } }).then(({ data, error }) => {
      if (error || !data) throw error ?? new Error(`createPaperwork returned no data`)
      return data.createPaperwork.link
    })
    const copied = copyWhenReady(link, `Link copied. It works for 14 days.`).then(
      () => true,
      (err) => {
        logError(`useSendPaperwork: copy failed`, err)
        return false
      },
    )
    try {
      await link
    } catch (err) {
      notifyError(err instanceof CombinedError ? err.graphQLErrors[0]?.message : `Couldn't create the paperwork link`)
      logError(`useSendPaperwork: create failed`, err)
      return
    }
    if (!(await copied)) notifySuccess(`Paperwork link created. Copy it from the Documents tab.`)
    onSent()
  }

  return {
    packets,
    loadingPackets: fetching && !data,
    patients,
    packetId,
    patientId,
    setPacketId: setChosenPacketId,
    setPatientId: setChosenPatientId,
    sending,
    send,
  }
}
